/**
 * chatUnfurl — station-chat link previews, client side.
 *
 * share-link-unfurl slug. A room message whose text carries one of our share
 * links gets a preview card under the bubble: the question, and a thumbnail of
 * the result. The same card Slack would draw for the same link, drawn by us,
 * for people who are already in the room.
 *
 * Following `chatFiles`'s decision, this is NOT a new message type. The message
 * on the wire is ordinary text; the preview is a render-time enrichment the
 * client resolves. Nothing changes for the helper, the room history, or anyone
 * on an older build — they see the link, which is what they see today.
 *
 * SAME-ORIGIN SHARE LINKS ONLY. Previewing an arbitrary pasted URL means the
 * server fetches it, which is an SSRF primitive aimed by whoever is in the
 * room. That is a separate feature with a separate threat model; see the
 * backlog note.
 */

// Our share links, as they appear in message text. Both the absolute form
// (what Share copies to the clipboard) and the root-relative one (what an
// in-app paste can produce). The id charset is `secrets.token_urlsafe`.
// No trailing lookahead: the id charset ends the match on its own, and a
// lookahead demanding whitespace-or-bracket after it FAILS ENTIRELY on
// "/embed/s/abc123." — every shorter backtrack is still followed by an id
// character, so a link at the end of a sentence previewed for nobody.
const SHARE_URL_RE =
  /(?:https?:\/\/[^\s<>"')]+)?\/embed\/s\/[A-Za-z0-9_-]+/g;

/**
 * The FIRST share link in `text`, or null.
 *
 * One per message on purpose. A message pasting five links would otherwise
 * stack five cards under one bubble and push the conversation off screen —
 * chat clients cap this for the same reason. First, not last, because a person
 * leads with the thing they are sharing and adds commentary after.
 */
export function shareLinkIn(text) {
  if (typeof text !== 'string' || !text) return null;
  SHARE_URL_RE.lastIndex = 0;              // the regex is /g and module-level
  const m = SHARE_URL_RE.exec(text);
  if (!m) return null;
  // No trimming needed: `)` from markdown link syntax and `.`/`,` from prose
  // are all outside the id charset, so the match already stopped before them.
  return m[0];
}

/**
 * Ask the BFF what to draw for `url`. Resolves to the preview object, or null.
 *
 * Never throws and never rejects: a preview is decoration on a message that is
 * already readable, so every failure path — offline, 404, malformed JSON, a
 * record that is not a shared snapshot — is the same "draw nothing" outcome.
 * A broken preview must not make a message look broken.
 */
export async function fetchUnfurl(url, { fetchImpl = fetch } = {}) {
  try {
    const res = await fetchImpl(
      `/api/v1/unfurl?url=${encodeURIComponent(url)}`,
      { credentials: 'include' },
    );
    if (!res.ok) return null;
    const body = await res.json();
    if (!body || typeof body !== 'object' || !body.url) return null;
    return body;
  } catch (e) {
    return null;
  }
}

/**
 * `text` with `url` taken out, tidied for display.
 *
 * Called ONLY once a preview has actually resolved (see `useLinkPreview`).
 * Stripping on sight would mean a link that fails to resolve — a deleted
 * record, an offline moment — vanishes from the message and leaves nothing
 * behind, which is worse than the raw URL it replaced. The card earns the
 * removal; it does not get it in advance.
 *
 * The message ON THE WIRE is untouched. This is a render-time edit, so the
 * room history, an older client, and anyone copying the text still have the
 * link.
 */
export function stripShareLink(text, url) {
  if (typeof text !== 'string' || !url) return text;
  let out = text.split(url).join(' ');
  // Markdown link syntax leaves `[label]()` behind once the href goes. Keep the
  // label — someone wrote it — and drop the empty parens.
  out = out.replace(/\[([^\]]*)\]\(\s*\)/g, '$1');
  // Collapse the hole the URL left, and any dangling "look:" / "see -" lead-in
  // that now points at nothing.
  out = out.replace(/[ \t]+/g, ' ').replace(/ ?\n ?/g, '\n').trim();
  out = out.replace(/[\s]*[:\-–—]$/, '').trim();
  return out;
}
